import { Reveal } from "../components/Reveal";
import { SEO } from "../components/SEO";
import { ExploreCarcass } from "../components/ExploreCarcass";
import { CTABand } from "../components/CTABand";
import { IMGS } from "../lib/img";
import { Layers, Link2, Droplets } from "lucide-react";

const SectionImage = ({ src, alt, children }) => (
  <div className="relative">
    <div className="absolute -left-4 -top-4 h-full w-full rounded-3xl border-2 border-gold/40" />
    <img src={src} alt={alt} loading="lazy" className="relative aspect-[4/3] w-full rounded-3xl object-cover shadow-xl" />
  </div>
);

export default function Carcass() {
  return (
    <>
      <SEO
        title="Carcass & Construction — The Strength Behind the Beauty | Paramount Kitchen"
        description="Inside every Paramount Kitchen: carefully selected plywood (19/16/9 mm), adhesive bonding, 50-ton hydraulic cold pressing, T3200-M precision panel cutting, edge banding, stainless-steel joinery and WPC base protection. Kangra, Himachal Pradesh."
      />

      <section className="relative overflow-hidden bg-royaldeep pb-24 pt-40 text-ivory md:pb-32 md:pt-52">
        <img
          src={IMGS.carcass}
          alt="Plywood cabinet carcass corner with stainless-steel screw joinery"
          className="absolute inset-0 h-full w-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-royaldeep/70 via-royaldeep/85 to-royaldeep" />
        <div className="relative mx-auto max-w-7xl px-6">
          <Reveal>
            <p className="eyebrow text-gold">Carcass &amp; Construction</p>
            <h1 className="text-balance mt-5 max-w-3xl font-display text-4xl font-medium leading-tight md:text-6xl">
              The Strength Behind the <span className="serif-accent text-gold">Beauty.</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg text-ivory/70">
              Your kitchen is only as strong as the structure you cannot see.
            </p>
          </Reveal>
        </div>
      </section>

      {/* SECTION 1 — MATERIAL SELECTION */}
      <section data-testid="carcass-material-selection" className="bg-ivory py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid items-center gap-14 lg:grid-cols-2">
            <Reveal>
              <p className="eyebrow text-golddim">Step 01 — Material Selection</p>
              <h2 className="text-balance mt-5 font-display text-4xl font-medium leading-tight text-ink md:text-5xl">
                It begins with <span className="serif-accent text-golddim">choosing right.</span>
              </h2>
              <p className="mt-6 text-lg leading-relaxed text-ink/70">
                We begin by selecting materials appropriate for different applications within the
                kitchen. Board thickness is not a one-size decision — it is selected according to
                the component and the job it must do.
              </p>
              <div className="mt-8 inline-flex items-center gap-3 rounded-full border border-gold/40 bg-sand px-6 py-3 text-sm font-semibold text-wood">
                <Layers className="h-4 w-4" /> 8 × 4 ft plywood sheets selected for production
              </div>
            </Reveal>
            <div className="grid grid-cols-3 gap-4 md:gap-6">
              {["19 mm", "16 mm", "9 mm"].map((t, i) => (
                <Reveal key={t} delay={i * 0.1}>
                  <div
                    data-testid={`thickness-card-${t.replace(" ", "")}`}
                    className={`flex flex-col items-center justify-center rounded-2xl border p-6 text-center transition-all duration-300 hover:-translate-y-1 hover:shadow-lg md:p-10 ${
                      i === 0 ? "border-gold bg-royal text-ivory" : "border-ink/10 bg-white text-ink"
                    }`}
                  >
                    <span className="font-display text-3xl font-medium md:text-5xl">{t}</span>
                    <span className="mt-3 text-[10px] font-bold uppercase tracking-[0.18em] opacity-60">
                      Selected per application
                    </span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2 — PLYWOOD LAMINATION */}
      <section data-testid="carcass-lamination" className="bg-sand py-24 md:py-32">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 lg:grid-cols-2">
          <Reveal className="order-2 lg:order-1">
            <p className="eyebrow text-golddim">Step 02 — Plywood Lamination</p>
            <h2 className="text-balance mt-5 font-display text-4xl font-medium leading-tight text-ink md:text-5xl">
              Built from <span className="serif-accent text-golddim">carefully bonded layers.</span>
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-ink/70">
              Our plywood components are laminated using adhesive and pressed under controlled
              pressure to create a strong, stable construction — plywood sheets, 0.8 mm laminate
              and high-grade adhesive, brought together deliberately.
            </p>
            <ul className="mt-8 space-y-3">
              {["Plywood sheets — selected per application", "0.8 mm laminate on both faces", "High-grade adhesive bonding", "Controlled pressing"].map((x) => (
                <li key={x} className="flex items-center gap-3 text-sm font-medium text-ink/75">
                  <span className="h-2 w-2 rotate-45 bg-gold" /> {x}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.15} className="order-1 lg:order-2">
            <div className="mx-auto max-w-md rounded-3xl border border-ink/10 bg-white p-8 shadow-xl">
              <p className="eyebrow text-golddim">Panel Cross-Section</p>
              <div className="mt-6 space-y-2">
                {[
                  { l: "LAMINATE", cls: "bg-gradient-to-b from-gold to-golddim text-royal", h: "h-12" },
                  { l: "ADHESIVE", cls: "bg-amber-300 text-royal", h: "h-5" },
                  { l: "PLYWOOD", cls: "bg-wood text-ivory ply-texture", h: "h-24" },
                  { l: "ADHESIVE", cls: "bg-amber-300 text-royal", h: "h-5" },
                  { l: "LAMINATE", cls: "bg-gradient-to-b from-gold to-golddim text-royal", h: "h-12" },
                ].map((row, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <div className={`flex-1 rounded-md ${row.h} ${row.cls} grid place-items-center`}>
                      <span className="text-[10px] font-bold tracking-[0.24em]">{row.l}</span>
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-6 text-center text-xs text-ink/45">
                Laminate, adhesive and plywood — bonded under controlled pressure.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SECTION 3 — HYDRAULIC COLD PRESSING */}
      <section data-testid="carcass-pressing" className="bg-ivory py-24 md:py-32">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 lg:grid-cols-2">
          <Reveal>
            <SectionImage
              src={IMGS.press}
              alt="50-ton automatic hydraulic cold press machine laminating plywood panels"
            />
          </Reveal>
          <Reveal delay={0.1}>
            <p className="eyebrow text-golddim">Step 03 — Hydraulic Cold Pressing</p>
            <h2 className="text-balance mt-5 font-display text-4xl font-medium leading-tight text-ink md:text-5xl">
              Pressure that <span className="serif-accent text-golddim">builds strength.</span>
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-ink/70">
              Laminated plywood sheets are kept overnight under a 50-ton automatic hydraulic cold
              press machine designed specifically for laminating and pressing.
            </p>
            <p className="mt-4 leading-relaxed text-ink/60">
              This controlled pressing helps achieve consistent bonding across the panel and a
              stable, well-bonded finished board — the quiet discipline behind a kitchen that holds
              together for years.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              {["50-ton press", "Overnight pressing", "Consistent bonding", "Stable panels"].map((c) => (
                <span key={c} className="rounded-full border border-ink/15 bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-ink/70">
                  {c}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* SECTION 4 — PRECISION PANEL CUTTING */}
      <section data-testid="carcass-cutting" className="bg-royal py-24 text-ivory md:py-32">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 lg:grid-cols-2">
          <Reveal className="order-2 lg:order-1">
            <p className="eyebrow text-gold">Step 04 — Precision Panel Cutting</p>
            <h2 className="text-balance mt-5 font-display text-4xl font-medium leading-tight md:text-5xl">
              Precision at <span className="serif-accent text-gold">every cut.</span>
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-ivory/70">
              After the pressing process, panels are precisely cut using a professional panel saw
              to achieve accurate dimensions and clean cutting.
            </p>
            <div className="mt-8 inline-flex items-center gap-4 rounded-2xl border border-gold/40 bg-gold/10 px-6 py-4">
              <span className="font-display text-2xl font-medium text-gold">T3200-M Classic</span>
              <span className="text-xs uppercase tracking-[0.18em] text-ivory/60">
                Professional panel saw
              </span>
            </div>
            <p className="mt-8 leading-relaxed text-ivory/60">
              Our cutting process is designed to minimise chipping and improve dimensional
              accuracy — so every panel fits the way it was engineered to.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="order-1 lg:order-2">
            <SectionImage src={IMGS.saw} alt="T3200-M Classic professional panel saw cutting a plywood panel" />
          </Reveal>
        </div>
      </section>

      {/* SECTION 5 — EDGE BANDING */}
      <section data-testid="carcass-edge-banding" className="bg-ivory py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal className="mx-auto max-w-2xl text-center">
            <p className="eyebrow text-golddim">Step 05 — Edge Banding</p>
            <h2 className="text-balance mt-5 font-display text-4xl font-medium leading-tight text-ink md:text-5xl">
              Every edge <span className="serif-accent text-golddim">matters.</span>
            </h2>
            <p className="mt-6 leading-relaxed text-ink/65">
              After cutting, the exposed edges of plywood components are processed through an
              edge-banding machine to provide a clean, protected and finished edge — helping
              protect the board and improving the appearance of every component.
            </p>
          </Reveal>
          <div className="mx-auto mt-14 grid max-w-4xl grid-cols-1 items-center gap-8 sm:grid-cols-[1fr_auto_1fr]">
            <Reveal>
              <div className="rounded-2xl border border-ink/10 bg-white p-6 text-center shadow-sm">
                <p className="eyebrow text-ink/45">Before</p>
                <div className="mt-5 border-y-4 border-dashed border-amber-700/60 bg-wood ply-texture py-8">
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-ivory/80">
                    Raw Plywood Edge
                  </span>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.1} className="text-center">
              <span className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-gold text-royal">
                <Link2 className="h-5 w-5 rotate-90" />
              </span>
              <p className="mt-3 text-xs font-bold uppercase tracking-[0.18em] text-ink/60">
                Edge Banding
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="rounded-2xl border border-gold/50 bg-white p-6 text-center shadow-md">
                <p className="eyebrow text-golddim">After</p>
                <div className="mt-5 rounded-lg bg-wood py-8">
                  <div className="mx-2 border-y-4 border-gold py-4">
                    <span className="text-xs font-bold uppercase tracking-[0.2em] text-ivory">
                      Protected Finished Edge
                    </span>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SECTION 6 — JOINERY */}
      <section data-testid="carcass-joinery" className="bg-parch py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal className="mx-auto max-w-2xl text-center">
            <p className="eyebrow text-golddim">Carcass Joinery</p>
            <h2 className="text-balance mt-5 font-display text-4xl font-medium leading-tight text-ink md:text-5xl">
              Beyond <span className="serif-accent text-golddim">minifix assembly.</span>
            </h2>
            <p className="mt-6 leading-relaxed text-ink/65">
              Many modular kitchens rely heavily on conventional knock-down/minifix-style assembly.
              Paramount Kitchen focuses on a stronger construction approach using adhesive bonding
              and high-grade stainless-steel screws for structural assembly.
            </p>
          </Reveal>
          <div className="mx-auto mt-14 grid max-w-5xl gap-8 md:grid-cols-2">
            <Reveal>
              <div className="h-full rounded-3xl border border-ink/10 bg-white p-9">
                <p className="eyebrow text-ink/40">Typical approach</p>
                <h3 className="mt-4 font-display text-2xl font-medium text-ink/70">
                  Conventional Knock-Down Assembly
                </h3>
                <p className="mt-4 text-sm leading-relaxed text-ink/55">
                  Standard modular construction commonly uses minifix-style knock-down fittings to
                  join carcass panels.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="relative h-full rounded-3xl border-2 border-gold bg-royal p-9 text-ivory shadow-xl">
                <span className="absolute -top-3.5 left-8 rounded-full bg-gold px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-royal">
                  Paramount Standard
                </span>
                <p className="eyebrow text-gold">Our approach</p>
                <h3 className="mt-4 font-display text-2xl font-medium">
                  Paramount Engineered Joinery
                </h3>
                <ul className="mt-4 space-y-2.5">
                  {[
                    "High-grade adhesive",
                    "High-grade stainless-steel screws",
                    "Secure structural joining",
                    "Carefully aligned components",
                  ].map((x) => (
                    <li key={x} className="flex items-center gap-3 text-sm text-ivory/80">
                      <span className="h-1.5 w-1.5 rotate-45 bg-gold" /> {x}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
          <Reveal className="mx-auto mt-10 max-w-3xl text-center">
            <p className="text-sm italic text-ink/50">
              Depending on the component and application, Paramount Kitchen uses adhesive bonding
              and high-grade stainless-steel screws to create secure and durable carcass
              assemblies.
            </p>
          </Reveal>
        </div>
      </section>

      {/* SECTION 7 — WPC PROTECTION */}
      <section data-testid="carcass-wpc" className="bg-ivory py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal className="mx-auto max-w-2xl text-center">
            <p className="eyebrow text-golddim">Base Unit Protection</p>
            <h2 className="text-balance mt-5 font-display text-4xl font-medium leading-tight text-ink md:text-5xl">
              Protection where it <span className="serif-accent text-golddim">matters most.</span>
            </h2>
            <p className="mt-6 leading-relaxed text-ink/65">
              Base units are installed with a suitable rebate/detailing approach, and a high-grade
              WPC sheet is incorporated on top and protective areas to help protect the structure
              from accidental water seepage from the stone countertop.
            </p>
          </Reveal>
          <div className="mt-14 flex flex-col items-center justify-center gap-4 lg:flex-row">
            {[
              { icon: Droplets, t: "WPC Protection" },
              { icon: Layers, t: "Engineered Carcass" },
              { icon: Link2, t: "Proper Edge Treatment" },
            ].map((x, i) => (
              <Reveal key={x.t} delay={i * 0.1} className="w-full lg:w-auto">
                <div className="flex items-center justify-center gap-3 rounded-2xl border border-ink/10 bg-white px-6 py-5 text-sm font-bold uppercase tracking-[0.12em] text-ink lg:px-8">
                  <x.icon className="h-5 w-5 text-golddim" /> {x.t}
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.3}>
              <span className="hidden font-display text-3xl text-golddim lg:block">=</span>
            </Reveal>
            <Reveal delay={0.35} className="w-full lg:w-auto">
              <div className="rounded-2xl bg-gold px-8 py-5 text-center text-sm font-bold uppercase tracking-[0.14em] text-royal shadow-lg">
                Added Protection
              </div>
            </Reveal>
          </div>
          <Reveal className="mx-auto mt-10 max-w-3xl text-center">
            <p className="text-sm text-ink/50">
              Designed to provide additional protection against accidental water exposure in
              vulnerable areas — not a claim that the kitchen is waterproof.
            </p>
          </Reveal>
        </div>
      </section>

      <ExploreCarcass />
      <CTABand />
    </>
  );
}
